import { useState, type FormEvent } from 'react';
import { Lock } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { PinInput } from '@/components/account/PinInput';
import { accountsService } from '@/services/accounts.service';
import { ApiError } from '@/services/http';
import { useAccountStore } from '@/store/account.store';

export interface AccountPinGateProps {
  /** Whether the account already has a parent PIN. Drives verify vs. set. */
  hasPin: boolean;
  /** Called once the PIN is verified (or freshly set) — unlocks the page. */
  onUnlock: () => void;
}

/**
 * AccountPinGate — blocks the Account page behind the family's parent PIN.
 *
 *   - PIN already set → "enter your PIN" verify flow (POST verify).
 *   - No PIN yet      → "set a PIN to continue" flow; we require the family
 *     to create one before the page opens, then unlock straight through.
 *
 * The unlocked flag lives in the parent (in-memory), so navigating away and
 * back re-locks — the PIN is required on every visit to the Account page.
 */
export function AccountPinGate({ hasPin, onUnlock }: AccountPinGateProps) {
  const setAccount = useAccountStore((s) => s.setAccount);
  const [digits, setDigits] = useState<string[]>(['', '', '', '']);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pin = digits.join('');
  const ready = pin.length === 4;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!ready || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      if (hasPin) {
        await accountsService.verifyParentPin(pin);
      } else {
        const updated = await accountsService.setParentPin(pin);
        setAccount(updated);
        toast.success('Parent PIN set.');
      }
      onUnlock();
    } catch (err) {
      if (hasPin && err instanceof ApiError && err.status === 401) {
        setError("That PIN didn't match. Try again.");
      } else {
        setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
      }
      setDigits(['', '', '', '']);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex justify-center py-8">
      <section className="w-full max-w-md rounded-2xl border border-or-slate-200 bg-white p-7 text-center">
        <span
          aria-hidden
          className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-or-orange-100 text-or-orange-700"
        >
          <Lock className="h-5 w-5" />
        </span>
        <h2 className="mt-4 font-display text-[20px] font-bold text-or-slate-900">
          {hasPin ? 'Enter your parent PIN' : 'Set a parent PIN'}
        </h2>
        <p className="mx-auto mt-1 max-w-sm text-[13px] text-or-slate-600">
          {hasPin
            ? 'Your account settings are protected. Enter your 4-digit parent PIN to continue.'
            : 'Protect your account settings with a 4-digit parent PIN. You’ll need it each time you open this page.'}
        </p>

        <form onSubmit={handleSubmit} className="mt-5 flex flex-col gap-4">
          <PinInput
            digits={digits}
            onDigitsChange={(next) => {
              setDigits(next);
              setError(null);
            }}
            disabled={submitting}
            autoFocus
          />
          {error ? <p className="text-[12px] font-medium text-or-rose-700">{error}</p> : null}
          <Button type="submit" disabled={!ready || submitting}>
            {submitting
              ? hasPin
                ? 'Checking…'
                : 'Saving…'
              : hasPin
                ? 'Unlock'
                : 'Set PIN & continue'}
          </Button>
        </form>

        {hasPin ? null : (
          <p className="mx-auto mt-3 max-w-sm text-[12px] text-or-slate-500">
            There’s no recovery flow — pick something memorable. You can change or remove it any
            time from Account Safety.
          </p>
        )}
      </section>
    </div>
  );
}
